/**
 * InterestStudentList Component
 * 
 * Displays student details for a specific presential course.
 * Allows status updates (pending/confirmed/cancelled) with color-coded indicators.
 * Shows payment status and allows sending payment links.
 * 
 * Requirements: 8.4, 8.5, 9.3, 9.5, 14.1, 14.7
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import { 
  ArrowLeft,
  Users, 
  RefreshCw, 
  AlertCircle, 
  Mail,
  Phone,
  CreditCard,
  Calendar,
  Clock,
  UserCheck,
  XCircle,
  Send,
  DollarSign,
  CheckCircle,
  Loader2,
  Trash2
} from 'lucide-react';
import { 
  getInterests, 
  updateInterestStatus, 
  deleteInterest,
  sendPaymentLinks 
} from '../services/courseInterestApi';


// Status configuration
const STATUS_CONFIG = {
  pending: {
    label: 'Pendiente',
    color: 'yellow',
    icon: Clock,
    bgClass: 'bg-yellow-500/20',
    textClass: 'text-yellow-400',
    borderClass: 'border-yellow-500/30',
  },
  confirmed: {
    label: 'Confirmado',
    color: 'emerald',
    icon: UserCheck,
    bgClass: 'bg-emerald-500/20',
    textClass: 'text-emerald-400',
    borderClass: 'border-emerald-500/30',
  },
  cancelled: {
    label: 'Cancelado',
    color: 'red',
    icon: XCircle,
    bgClass: 'bg-red-500/20',
    textClass: 'text-red-400',
    borderClass: 'border-red-500/30',
  },
};

// Payment status configuration
const PAYMENT_STATUS_CONFIG = {
  pending: { label: 'Sin enlace', color: 'gray' },
  link_sent: { label: 'Enlace enviado', color: 'blue' },
  paid: { label: 'Pagado', color: 'emerald' },
  expired: { label: 'Expirado', color: 'red' },
};

/**
 * InterestStudentList Component
 * 
 * @param {number} courseId - Course ID to display students for
 * @param {string} courseName - Course name
 * @param {Function} onBack - Callback to go back to overview
 * @param {Function} onRefresh - Callback to refresh parent data
 * @param {string} className - Additional CSS classes
 */
export default function InterestStudentList({
  courseId,
  courseName,
  onBack,
  onRefresh,
  className = '',
}) {
  // Data state
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Action states
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  
  // Payment link modal state
  const [showPaymentModal, setShowPaymentModal] = useState(null);
  const [paymentPrice, setPaymentPrice] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentLocation, setPaymentLocation] = useState('');
  const [sendingPaymentLinks, setSendingPaymentLinks] = useState(false);
  
  // Success/error messages
  const [actionMessage, setActionMessage] = useState(null);

  /**
   * Fetch students for this course
   */
  const fetchStudents = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await getInterests({ courseId });
      setStudents(result.interests || []);
    } catch (err) {
      console.error('Failed to fetch students:', err);
      setError(err.message || 'Error al cargar los estudiantes');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  /**
   * Handle status update
   */
  const handleStatusUpdate = async (studentId, newStatus) => {
    setUpdatingId(studentId);
    setActionMessage(null);
    
    try {
      await updateInterestStatus(studentId, newStatus);
      setActionMessage({ type: 'success', text: 'Estado actualizado correctamente' });
      await fetchStudents();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to update status:', err);
      setActionMessage({ type: 'error', text: err.message || 'Error al actualizar el estado' });
    } finally {
      setUpdatingId(null);
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = async () => {
    if (!showDeleteModal || !deleteReason.trim()) return;
    
    setDeletingId(showDeleteModal);
    setActionMessage(null);
    
    try {
      await deleteInterest(showDeleteModal, deleteReason);
      setActionMessage({ type: 'success', text: 'Registro eliminado correctamente' });
      setShowDeleteModal(null);
      setDeleteReason('');
      await fetchStudents();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to delete:', err);
      setActionMessage({ type: 'error', text: err.message || 'Error al eliminar el registro' });
    } finally {
      setDeletingId(null);
    }
  };


  /**
   * Handle send payment links
   */
  const handleSendPaymentLinks = async () => {
    if (!showPaymentModal || !paymentPrice) return;
    
    setSendingPaymentLinks(true);
    setActionMessage(null);
    
    try {
      const result = await sendPaymentLinks(
        courseId,
        showPaymentModal.groupNumber,
        parseInt(paymentPrice, 10),
        paymentDate,
        paymentLocation
      );
      
      setActionMessage({ 
        type: 'success', 
        text: `Se enviaron ${result.linksSent} enlaces de pago correctamente` 
      });
      setShowPaymentModal(null);
      setPaymentPrice('');
      setPaymentDate('');
      setPaymentLocation('');
      await fetchStudents();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to send payment links:', err);
      setActionMessage({ type: 'error', text: err.message || 'Error al enviar los enlaces de pago' });
    } finally {
      setSendingPaymentLinks(false);
    }
  };

  /**
   * Group students by group number
   */
  const groupedStudents = students.reduce((acc, student) => {
    const groupKey = student.groupNumber || 'ungrouped';
    if (!acc[groupKey]) {
      acc[groupKey] = [];
    }
    acc[groupKey].push(student);
    return acc;
  }, {});

  // Sort groups: numbered groups first, then ungrouped
  const sortedGroups = Object.keys(groupedStudents).sort((a, b) => {
    if (a === 'ungrouped') return 1;
    if (b === 'ungrouped') return -1;
    return parseInt(a, 10) - parseInt(b, 10);
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className={`${className}`}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <motion.button
            onClick={onBack}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 bg-white/10 hover:bg-white/20 rounded-lg
                       text-white/80 transition-all"
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <Users className="w-6 h-6 text-blue-400" />
              {courseName}
            </h2>
            <p className="text-white/60 text-sm mt-1">
              {students.length} estudiantes interesados
            </p>
          </div>
        </div>
        
        <motion.button
          onClick={fetchStudents}
          disabled={loading}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="px-4 py-2 bg-white/10 hover:bg-white/20 
                     text-white/80 rounded-lg border border-white/10
                     flex items-center gap-2 text-sm font-medium
                     disabled:opacity-50 disabled:cursor-not-allowed
                     transition-all"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </motion.button>
      </div>

      {/* Action Message */}
      <AnimatePresence>
        {actionMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`mb-4 p-3 rounded-xl flex items-center gap-2 ${
              actionMessage.type === 'success' 
                ? 'bg-emerald-500/20 border border-emerald-500/30' 
                : 'bg-red-500/20 border border-red-500/30'
            }`}
          >
            {actionMessage.type === 'success' 
              ? <CheckCircle className="w-4 h-4 text-emerald-400" />
              : <AlertCircle className="w-4 h-4 text-red-400" />
            }
            <span className={actionMessage.type === 'success' ? 'text-emerald-400' : 'text-red-400'}>
              {actionMessage.text}
            </span>
            <button 
              onClick={() => setActionMessage(null)}
              className="ml-auto text-white/50 hover:text-white/80"
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading State */}
      {loading && (
        <motion.div
          className="flex flex-col items-center justify-center py-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="w-12 h-12 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin mb-4" />
          <p className="text-white/60">Cargando estudiantes...</p>
        </motion.div>
      )}

      {/* Error State */}
      {error && !loading && (
        <motion.div
          className="flex flex-col items-center justify-center py-16 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="p-4 rounded-full bg-red-500/20 mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Error</h3>
          <p className="text-white/60 mb-6">{error}</p>
          <motion.button
            onClick={fetchStudents}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 
                       text-white font-semibold rounded-xl"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            Reintentar
          </motion.button>
        </motion.div>
      )}


      {/* Student Groups */}
      {!loading && !error && (
        <motion.div
          className="space-y-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {sortedGroups.map((groupKey) => {
            const groupStudents = groupedStudents[groupKey];
            const isUngrouped = groupKey === 'ungrouped';
            const allConfirmed = groupStudents.every(s => s.status === 'confirmed');
            const hasPaymentLinks = groupStudents.some(s => s.paymentStatus && s.paymentStatus !== 'pending');
            
            return (
              <div key={groupKey} className="space-y-3">
                {/* Group Header */}
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    {isUngrouped ? (
                      <>
                        <Clock className="w-5 h-5 text-amber-400" />
                        Esperando grupo
                      </>
                    ) : (
                      <>
                        <UserCheck className="w-5 h-5 text-emerald-400" />
                        Grupo #{groupKey}
                      </>
                    )}
                    <span className="text-sm text-white/50 font-normal">
                      ({groupStudents.length} estudiantes)
                    </span>
                  </h3>
                  
                  {/* Send Payment Links Button - Only for formed groups */}
                  {!isUngrouped && allConfirmed && !hasPaymentLinks && (
                    <motion.button
                      onClick={() => setShowPaymentModal({ groupNumber: parseInt(groupKey, 10) })}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500
                                 hover:from-blue-600 hover:to-cyan-600
                                 text-white text-sm font-medium rounded-lg
                                 flex items-center gap-2 shadow-lg shadow-blue-500/30"
                    >
                      <Send className="w-4 h-4" />
                      Enviar Enlaces de Pago
                    </motion.button>
                  )}
                </div>

                {/* Student Cards */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {groupStudents.map((student) => {
                    const statusConfig = STATUS_CONFIG[student.status] || STATUS_CONFIG.pending;
                    const StatusIcon = statusConfig.icon;
                    const paymentConfig = PAYMENT_STATUS_CONFIG[student.paymentStatus] || PAYMENT_STATUS_CONFIG.pending;
                    
                    return (
                      <motion.div
                        key={student.id}
                        variants={itemVariants}
                        className={`p-4 rounded-xl bg-white/5 border ${statusConfig.borderClass}
                                    hover:bg-white/10 transition-all`}
                      >
                        {/* Student Header */}
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <h4 className="font-semibold text-white">{student.fullName}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium
                                              ${statusConfig.bgClass} ${statusConfig.textClass}`}>
                                <StatusIcon className="w-3 h-3 inline mr-1" />
                                {statusConfig.label}
                              </span>
                              {student.paymentStatus && student.paymentStatus !== 'pending' && (
                                <span className={`px-2 py-0.5 rounded text-xs font-medium
                                                bg-${paymentConfig.color}-500/20 text-${paymentConfig.color}-400`}>
                                  <DollarSign className="w-3 h-3 inline mr-1" />
                                  {paymentConfig.label}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {/* Delete Button */}
                          <button
                            onClick={() => setShowDeleteModal(student.id)}
                            className="p-1.5 text-white/30 hover:text-red-400 
                                       hover:bg-red-500/20 rounded transition-all"
                            title="Eliminar registro"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-1.5 text-sm mb-3">
                          <div className="flex items-center gap-2 text-white/70">
                            <Mail className="w-4 h-4 text-white/40" />
                            <a href={`mailto:${student.email}`} className="hover:text-blue-400">
                              {student.email}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-white/70">
                            <Phone className="w-4 h-4 text-white/40" />
                            <a href={`tel:${student.phone}`} className="hover:text-blue-400">
                              {student.phone}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-white/70">
                            <CreditCard className="w-4 h-4 text-white/40" />
                            {student.rut}
                          </div>
                          <div className="flex items-center gap-2 text-white/50 text-xs">
                            <Calendar className="w-3 h-3" />
                            {new Date(student.createdAt).toLocaleDateString('es-CL', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>

                        {/* Status Update Buttons */}
                        <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                          <span className="text-xs text-white/50 mr-2">Cambiar estado:</span>
                          {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                            const Icon = config.icon;
                            const isActive = student.status === status;
                            const isUpdating = updatingId === student.id;
                            
                            return (
                              <button
                                key={status}
                                onClick={() => !isActive && handleStatusUpdate(student.id, status)}
                                disabled={isActive || isUpdating}
                                className={`p-1.5 rounded transition-all ${
                                  isActive 
                                    ? `${config.bgClass} ${config.textClass} cursor-default`
                                    : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white/80'
                                } disabled:opacity-50`}
                                title={config.label}
                              >
                                {isUpdating ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Icon className="w-4 h-4" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </motion.div>
      )}


      {/* Empty State */}
      {!loading && !error && students.length === 0 && (
        <motion.div
          className="flex flex-col items-center justify-center py-16 text-center"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="p-4 rounded-full bg-blue-500/20 mb-4">
            <Users className="w-8 h-8 text-blue-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">
            No hay estudiantes interesados
          </h3>
          <p className="text-white/60">
            Aún no hay registros de interés para este curso.
          </p>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDeleteModal(null)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="w-full max-w-md bg-slate-900 rounded-2xl shadow-2xl border border-white/10 p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Trash2 className="w-5 h-5 text-red-400" />
                  Eliminar Registro
                </h3>
                <p className="text-white/70 mb-4">
                  ¿Estás seguro de que deseas eliminar este registro? Esta acción no se puede deshacer.
                </p>
                <div className="mb-4">
                  <label className="block text-sm text-white/60 mb-2">
                    Razón de la eliminación *
                  </label>
                  <textarea
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder="Ingresa la razón..."
                    className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl
                               text-white placeholder-white/30 text-sm
                               focus:outline-none focus:ring-2 focus:ring-red-500/50"
                    rows={3}
                  />
                </div>
                <div className="flex items-center gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowDeleteModal(null);
                      setDeleteReason('');
                    }}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 
                               text-white/80 rounded-lg text-sm font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={!deleteReason.trim() || deletingId}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 
                               text-white rounded-lg text-sm font-medium
                               flex items-center gap-2
                               disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deletingId ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                    Eliminar
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Payment Links Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowPaymentModal(null)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
              <div className="w-full max-w-md bg-slate-900 rounded-2xl shadow-2xl border border-white/10 p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Send className="w-5 h-5 text-blue-400" />
                  Enviar Enlaces de Pago
                </h3>
                <p className="text-white/70 mb-4">
                  Se enviarán enlaces de pago individuales a los {groupedStudents[showPaymentModal.groupNumber]?.length || 4} estudiantes del Grupo #{showPaymentModal.groupNumber}.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm text-white/60 mb-2">
                      Precio del curso (CLP) *
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                      <input
                        type="number"
                        value={paymentPrice}
                        onChange={(e) => setPaymentPrice(e.target.value)}
                        placeholder="50000"
                        className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl
                                   text-white placeholder-white/30 text-sm
                                   focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-white/60 mb-2">
                      Fecha del curso (opcional)
                    </label>
                    <input
                      type="date"
                      value={paymentDate}
                      onChange={(e) => setPaymentDate(e.target.value)}
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl
                                 text-white text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-white/60 mb-2">
                      Ubicación del curso (opcional)
                    </label>
                    <input
                      type="text"
                      value={paymentLocation}
                      onChange={(e) => setPaymentLocation(e.target.value)}
                      placeholder="Instituto Valparaíso, Av. Principal 123"
                      className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl
                                 text-white placeholder-white/30 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowPaymentModal(null);
                      setPaymentPrice('');
                      setPaymentDate('');
                      setPaymentLocation('');
                    }}
                    className="px-4 py-2 bg-white/10 hover:bg-white/20 
                               text-white/80 rounded-lg text-sm font-medium"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSendPaymentLinks}
                    disabled={!paymentPrice || sendingPaymentLinks}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500
                               hover:from-blue-600 hover:to-cyan-600
                               text-white rounded-lg text-sm font-medium
                               flex items-center gap-2
                               disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingPaymentLinks ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    Enviar Enlaces
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

InterestStudentList.propTypes = {
  courseId: PropTypes.number.isRequired,
  courseName: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
  className: PropTypes.string,
};
